import { ReactElement, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { useMatomo } from '@jonkoops/matomo-tracker-react';

import { COMPANY_ADDRESS, COMPANY_NAME, COMPANY_TELEPHONE, EMAIL_INFO } from '../../shared/constants';

import './ComplaintsDialog.scss';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Osano: any;
  }
}

export interface ComplaintsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const ComplaintsDialog = ({ isOpen, onClose }: ComplaintsDialogProps): ReactElement => {
  const { trackPageView } = useMatomo();
  useEffect(() => {
    if (isOpen) {
      trackPageView({
        href: 'complaints-procedure',
      });
    }
  }, [isOpen, trackPageView]);

  return (
    <Dialog
      data-testid="complaints-dialog"
      fullScreen={false}
      open={isOpen}
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="company-complaints-title"
    >
      <DialogContent className="App-complaints">
        <div className="App-complaints-title" id="company-complaints-title">
          <Typography variant="body1">Omnigen Biodata Ltd</Typography>
          <Typography variant="h5">Omnigen&apos;s Complaints Procedure</Typography>
        </div>
        <Typography variant="body2" paragraph>
          This procedure sets out what you should do if you are considering making a complaint and how we will respond
          to your complaint.
        </Typography>
        <Typography variant="h6" paragraph>
          1. WHO WE ARE
        </Typography>
        <Typography variant="body2" paragraph>
          We are Omnigen Biodata Ltd (herein referred to as Omnigen), a private limited company (registered number
          10565653).
        </Typography>
        <Typography variant="body2" paragraph>
          Our registered address is Four Station Square, Cambridge, CB1 2GE.
        </Typography>

        <Typography variant="h6" paragraph>
          2. OUR COMMITMENT TO YOU
        </Typography>

        <Typography variant="body2" paragraph>
          We are committed to providing a high quality, transparent and accessible service to everyone we deal with. In
          order to do this, we need you to tell us when we get things wrong. We want to help resolve your complaint as
          quickly as possible.
        </Typography>
        <Typography variant="body2" paragraph>
          We handle any expression of dissatisfaction which calls for a response as a complaint. We treat all complaints
          promptly and seriously. We listen to your complaints, and learn from them so that we can continuously improve.
          For the purposes of this procedure, a complaint is defined as an expression of dissatisfaction, whether
          justified or not.
        </Typography>
        <Typography variant="body2" paragraph>
          You can expect to be treated with courtesy, respect and fairness at all times. We expect that you will also
          treat our Company Personnel dealing with your complaint with the same courtesy, respect and fairness.
        </Typography>
        <Typography variant="body2" paragraph>
          All complaints received will be dealt with confidentially within Omnigen, and in accordance with data
          protection laws (for example, the General Data Protection Regulation), subject to the need to disclose
          information as required by statutory authorities, and/or as a result of statutory, regulatory or legal
          obligations placed on Omnigen.
        </Typography>

        <Typography variant="h6" paragraph>
          3. WHAT DOES THIS COMPLAINTS PROCEDURE COVER?
        </Typography>
        <Typography variant="body2" paragraph>
          This procedure covers complaints about studies, products, procedures or services provided by Omnigen. This
          would include complaints about the conduct of a study or standard of service you should expect from us; the
          behaviour of our Company Personnel in delivering a study, product, procedure or service; or any action, or
          lack of action, by our Company Personnel or others engaged on Omnigen business.
        </Typography>
        <Typography variant="body2" paragraph>
          This complaints procedure does not cover comments about our policies or policy decisions; or dissatisfaction
          or complaints expressed with our policies or decisions about individual cases or requests for legal advice and
          assistance; or matters that have already been fully investigated through this complaints procedure; or
          anonymous complaints.
        </Typography>

        <Typography variant="h6" paragraph>
          4. HOW DO I MAKE A COMPLAINT?
        </Typography>
        <Typography variant="body2" paragraph>
          If you wish to make a complaint, you can do so by email or letter.
        </Typography>
        <Typography variant="body2" paragraph>
          We want to make our complaints policy easy to use and accessible to all. We will take reasonable steps to
          accommodate any reasonable adjustments you may have, in order to help you to access our complaints procedure
          or receive responses to complaints in other formats (e.g., by telephone), and provide such assistance as you
          may reasonably require.
        </Typography>
        <Typography variant="body2" paragraph>
          If you have a disability, and need a reasonable adjustment to ensure you can raise your complaint with us, you
          can contact us alternatively by telephone, and then one of our Company Personnel will help you by writing out
          your complaint.
        </Typography>
        <Typography variant="body2" paragraph>
          Our contact details are in the “Contacting Us” section at the end of this document. If you require different
          adjustments, please let us know and we will try and put those arrangements in place where we can.
        </Typography>

        <Typography variant="h6" paragraph>
          5. CAN SOMEONE ELSE MAKE A COMPLAINT ON MY BEHALF?
        </Typography>
        <Typography variant="body2" paragraph>
          You may wish to have someone else (a third party) act on your behalf in making a complaint. A third party is
          any person or organisation acting on your behalf, or making enquiries for you. For example, a third party
          might be an advice organisation, or a professional such as a doctor or solicitor, or a family member or
          friend.
        </Typography>
        <Typography variant="body2" paragraph>
          Where a third party is helping you with a particular complaint, we will need your written consent to that
          effect, giving them permission to act on your behalf. Where we have received this written permission, we will
          endeavour to take all possible steps to keep the third party informed of progress on your complaint.
        </Typography>
        <Typography variant="h6" paragraph>
          6. I’VE MADE A COMPLAINT – WHAT HAPPENS NOW?
        </Typography>
        <Typography variant="body2" paragraph>
          We have a two-stage complaints handling procedure, which is explained below.
        </Typography>
        <Typography variant="body2" paragraph>
          At each stage it will help us to resolve your complaint quickly if you can give us as much clarity and detail
          as possible, including providing any documents and correspondence and stating that you are making a complaint.
          If we do not have all the details required to deal with the complaint, we may contact you and ask you for
          further information.
        </Typography>
        <Typography variant="body1" paragraph>
          Stage 1
        </Typography>
        <Typography variant="body2" paragraph>
          This is the first opportunity for us to resolve your dissatisfaction. We expect the majority of complaints to
          be resolved at this stage.
        </Typography>
        <Typography variant="body2" paragraph>
          We will acknowledge receipt of a written complaint within five (5) business days where we have a return
          address, and we will send a full response within 20 business days of receiving a complaint. In a few cases we
          will not be able to send a full reply within 20 business days of receipt – for example, if your complaint is
          very complex. If this happens, we will tell you the reason why, and let you know when we will be able to reply
          in full, keeping you fully informed of progress.
        </Typography>

        <Typography variant="body2" paragraph>
          If you make a complaint in person to Omnigen Company Personnel (at an event or meeting), we will acknowledge
          it within five (5) business days thereafter. We will then deal with your complaint in accordance with our
          policy for written complaints.
        </Typography>

        <Typography variant="body1" paragraph>
          Stage 2
        </Typography>
        <Typography variant="body2" paragraph>
          If you are dissatisfied with the response at stage 1, you may request a review. This will be carried out by an
          Omnigen Director or Chief Investigator (for study-specific complaints).
        </Typography>
        <Typography variant="body2" paragraph>
          We will acknowledge your request for a complaints review within five (5) business days of receiving a request.
          We will send a full response within 20 business days of receiving a request for a complaints review.
        </Typography>

        <Typography variant="body1" paragraph>
          Extending time limits
        </Typography>
        <Typography variant="body2" paragraph>
          We aim to complete our investigation into all complaints received within the timescales set out above.
          However, in a some cases – for example, if a complaint is very complex or requires further investigation – it
          may be necessary to extend the time limit to ensure we have all the information necessary to deal properly
          with it.
        </Typography>
        <Typography variant="body2" paragraph>
          If this is the case we will keep you informed of progress with the investigation, the reasons for the delay,
          and inform you of next steps.
        </Typography>

        <Typography variant="h6" paragraph>
          7. WHAT WILL OMNIGEN DO, TO REMEDY MY COMPLAINT?
        </Typography>
        <Typography variant="body2" paragraph>
          When we get things wrong we will act to:
        </Typography>
        <ul>
          <Typography variant="body2" component="li">
            Accept responsibility and apologise
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Explain what went wrong and why
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Put things right by making any changes required
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Learn lessons from mistakes and change policies and practices where proportionate and sensible to do so
          </Typography>
        </ul>

        <Typography variant="body2" paragraph>
          The action we take to put matters right (i.e., redress) in response to a complaint can include any combination
          of the remedies set out in the list below. The general principle we follow is that you should, so far as
          possible, be put in the position you would have been in, had things not gone wrong.
        </Typography>
        <Typography variant="body2" paragraph>
          The remedy applied will be proportionate and appropriate to the failure in a study, product, procedure or
          service, and will take into account what redress you have sought in your complaint.
        </Typography>
        <Typography variant="body2" paragraph>
          Possible actions we might take, in order to remedy your complaint:
        </Typography>
        <ul>
          <Typography variant="body2" component="li">
            A full apology, explaining what happened and/or what went wrong (an apology is not an acceptance of
            liability under the Compensation Act 2006)
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Remedial action, which may include reviewing or changing a decision on the study, product, procedure or
            service provided to an individual complainant
          </Typography>
        </ul>

        <ul>
          <Typography variant="body2" component="li">
            Provide the study, product, procedure or service required in first instance (immediately, if appropriate)
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Putting things right (for example a change of procedure to prevent future difficulties of a similar kind,
            either for the complainant or others)
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Training or supervising Company Personnel; or a combination of both
          </Typography>
        </ul>
        <Typography variant="h6" paragraph>
          8. WHAT HAPPENS IF I AM NOT SATISFIED WITH THE WAY MY COMPLAINT HAS BEEN DEALT WITH?
        </Typography>
        <Typography variant="body2" paragraph>
          We hope that by following the two-stage process we describe above, we will be able to respond to your
          complaint so that you are satisfied with the outcome and any remedy we have applied.
        </Typography>
        <Typography variant="body2" paragraph>
          If, however, you remain dissatisfied, you can ask to have your complaint reviewed by an appropriate
          independent agency. The appropriate independent agency will assess whether there is evidence of failure or
          maladministration on our part.
        </Typography>

        <Typography variant="h6" paragraph>
          9. VEXATIOUS AND REPETITIVE COMPLAINTS, AND UNREASONABLE OR UNACCEPTABLE BEHAVIOUR
        </Typography>
        <Typography variant="body2" paragraph>
          All complaints will be dealt with in accordance with this complaints procedure. However, unreasonable or
          abusive complaint behaviour including vexatious and/or repetitive complaints may occur form time to time.
          Difficulties in handling such situations can place strain on time and resources and can be stressful for
          Company Personnel who have to deal with these complex and challenging issues.
        </Typography>
        <Typography variant="body2" paragraph>
          We have an internal policy for managing managing vexatious complaints, unreasonable or unacceptable behaviour,
          which sets out how we will respond to these situations.
        </Typography>

        <Typography variant="h6" paragraph>
          10. HOW WILL YOU RECORD MY COMPLAINT?
        </Typography>
        <Typography variant="body2" paragraph>
          We will keep a record of the details of your complaint, along with the outcomes and actions taken. We record
          all complaints we receive and collate data from them to help us understand what types of problems are most
          common, and how well we are doing to resolve them, and to see if we can improve the way we do things.
        </Typography>
        <Typography variant="body2" paragraph>
          We value your feedback and expect to use it to help us to:
        </Typography>
        <ul>
          <Typography variant="body2" component="li">
            Get things right in the future if we have not done so already
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Be more open and accountable
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Act fairly and proportionately
          </Typography>
        </ul>
        <ul>
          <Typography variant="body2" component="li">
            Seek continuous improvement
          </Typography>
        </ul>
        <Typography variant="body2" paragraph>
          We will handle your information so that it is only processed and retained appropriately and legally, in line
          with data protection legislation.
        </Typography>

        <Typography variant="h6" paragraph>
          11. QUALITY OF SERVICE AND COMMENTS
        </Typography>
        <Typography variant="body2" paragraph>
          Quality of service is an important measure for us of our effectiveness. As well as learning from your
          complaints, we are also interested in ideas you may have on how we might do things better. We would also like
          you to tell us when we do things well.
        </Typography>
        <Typography variant="body2" paragraph>
          Learning from your comments is a powerful way of helping continuous improvement at Omnigen and enable us to
          better deliver to our values, studies and standards. You can make your comments by contacting any members of
          our Company Personnel, or by using the contact details in the “Contacting Us” section below. Your comments
          will be passed on to the relevant team and we will use them to help improve the way we do things.
        </Typography>

        <Typography variant="h6" paragraph>
          12. CONTACTING US
        </Typography>
        <Typography variant="body2" paragraph>
          All comments, complaints and requests for review under our complaints procedure can be sent:
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>By post:</strong> {COMPANY_NAME}, {COMPANY_ADDRESS}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>By email:</strong> {EMAIL_INFO}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>By telephone:</strong> {COMPANY_TELEPHONE}
        </Typography>
        <Typography variant="body2" paragraph>
          This complaints procedure was last updated in January 2025.
        </Typography>

        <Typography variant="h6" paragraph>
          13. REFERENCE
        </Typography>
        <Typography variant="body2" paragraph>
          <span className="italicText">Compensation Act 2006.</span> Available at{' '}
          <a href="https://www.legislation.gov.uk/ukpga/2006/29/contents">
            https://www.legislation.gov.uk/ukpga/2006/29/contents
          </a>
          . Accessed 05Dec2024
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose} color="primary" data-testid="complaints-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplaintsDialog;
